import { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

type UseBarcodeScannerOptions = {
    elementId: string;
    enabled: boolean;
    onScan: (value: string) => void;
};

type ScannerStatus = 'idle' | 'starting' | 'scanning' | 'denied' | 'error';

export type CaptureScanResult =
    | { ok: true }
    | { ok: false; reason: 'not_ready' | 'capture_failed' | 'decode_failed'; message?: string };

const SCANNER_FORMATS = [
    Html5QrcodeSupportedFormats.QR_CODE,
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
];

/** Match CSS .mpc-scanner-frame inset (~12%) so shaded qrbox aligns with overlay. */
const qrboxSize = (viewfinderWidth: number, viewfinderHeight: number) => {
    const width = Math.floor(viewfinderWidth * 0.76);
    const height = Math.floor(viewfinderHeight * 0.5);
    return {
        width: Math.max(160, width),
        height: Math.max(100, height),
    };
};

const startWithCameraFallback = async (
    scanner: Html5Qrcode,
    onSuccess: (decodedText: string) => void
) => {
    // No aspectRatio — let the library fill the container; CSS forces video cover.
    const config = {
        fps: 10,
        qrbox: qrboxSize,
        disableFlip: false,
    };

    const onFail = () => undefined;

    try {
        await scanner.start({ facingMode: 'environment' }, config, onSuccess, onFail);
        return;
    } catch {
        // PC / no rear camera
    }

    try {
        await scanner.start({ facingMode: 'user' }, config, onSuccess, onFail);
        return;
    } catch {
        // first available device
    }

    const cameras = await Html5Qrcode.getCameras();
    if (!cameras.length) {
        throw new Error('No camera found');
    }
    await scanner.start(cameras[0].id, config, onSuccess, onFail);
};

const findScannerVideo = (elementId: string): HTMLVideoElement | null => {
    const root = document.getElementById(elementId);
    if (!root) return null;
    const videos = Array.from(root.querySelectorAll('video')) as HTMLVideoElement[];
    return (
        videos.find((v) => v.readyState >= 2 && (v.videoWidth > 0 || v.clientWidth > 0)) ||
        videos[0] ||
        null
    );
};

const waitForVideoFrame = (video: HTMLVideoElement, timeoutMs = 2500): Promise<boolean> =>
    new Promise((resolve) => {
        if (video.readyState >= 2 && video.videoWidth > 0) {
            resolve(true);
            return;
        }

        let settled = false;
        const done = (ok: boolean) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(ok);
        };

        const onReady = () => {
            if (video.videoWidth > 0) done(true);
        };

        const cleanup = () => {
            video.removeEventListener('loadeddata', onReady);
            video.removeEventListener('playing', onReady);
            window.clearTimeout(timer);
        };

        video.addEventListener('loadeddata', onReady);
        video.addEventListener('playing', onReady);

        if ('requestVideoFrameCallback' in video) {
            (
                video as HTMLVideoElement & {
                    requestVideoFrameCallback: (cb: () => void) => number;
                }
            ).requestVideoFrameCallback(() => done(video.videoWidth > 0));
        }

        const timer = window.setTimeout(() => done(video.videoWidth > 0 || video.clientWidth > 0), timeoutMs);
    });

const grabVideoFrameBlob = async (video: HTMLVideoElement): Promise<Blob | null> => {
    const width = video.videoWidth || video.clientWidth;
    const height = video.videoHeight || video.clientHeight;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    try {
        ctx.drawImage(video, 0, 0, width, height);
    } catch {
        return null;
    }

    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });
};

const ensureOffscreenDecodeHost = (tempId: string): HTMLElement => {
    let el = document.getElementById(tempId);
    if (!el) {
        el = document.createElement('div');
        el.id = tempId;
        document.body.appendChild(el);
    }
    // display:none breaks html5-qrcode scanFile on many browsers (incl. mobile WebKit)
    el.setAttribute(
        'style',
        'position:fixed;left:-10000px;top:0;width:480px;height:360px;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;'
    );
    return el;
};

export const useBarcodeScanner = ({ elementId, enabled, onScan }: UseBarcodeScannerOptions) => {
    const [status, setStatus] = useState<ScannerStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isDecodingFile, setIsDecodingFile] = useState(false);
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const onScanRef = useRef(onScan);
    const handledRef = useRef(false);

    useEffect(() => {
        onScanRef.current = onScan;
    }, [onScan]);

    const emitScan = useCallback((value: string) => {
        const trimmed = value.trim();
        if (!trimmed || handledRef.current) return;
        handledRef.current = true;
        onScanRef.current(trimmed);
    }, []);

    const stopScanner = useCallback(async () => {
        const scanner = scannerRef.current;
        if (!scanner) return;

        try {
            if (scanner.isScanning) {
                await scanner.stop();
            }
            scanner.clear();
        } catch {
            // ignore stop race conditions
        } finally {
            scannerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            void stopScanner();
            setStatus('idle');
            handledRef.current = false;
            return;
        }

        let cancelled = false;
        handledRef.current = false;

        const start = async () => {
            setStatus('starting');
            setErrorMessage('');

            try {
                await stopScanner();
                const scanner = new Html5Qrcode(elementId, {
                    formatsToSupport: SCANNER_FORMATS,
                    verbose: false,
                });
                scannerRef.current = scanner;

                await startWithCameraFallback(scanner, (decodedText) => {
                    if (cancelled) return;
                    emitScan(decodedText);
                });

                if (!cancelled) {
                    setStatus('scanning');
                }
            } catch (err: unknown) {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : String(err);
                const denied =
                    /NotAllowedError|Permission|denied|NotAllowed/i.test(message) ||
                    message.toLowerCase().includes('permission');
                setStatus(denied ? 'denied' : 'error');
                setErrorMessage(message);
            }
        };

        void start();

        return () => {
            cancelled = true;
            void stopScanner();
        };
    }, [enabled, elementId, stopScanner, emitScan]);

    const decodeImageFile = useCallback(
        async (file: File): Promise<CaptureScanResult> => {
            if (!file.type.startsWith('image/')) {
                return { ok: false, reason: 'decode_failed', message: 'Unsupported file type' };
            }

            setIsDecodingFile(true);
            setErrorMessage('');

            const tempId = `${elementId}-file-decode`;
            ensureOffscreenDecodeHost(tempId);

            const decoder = new Html5Qrcode(tempId, {
                formatsToSupport: SCANNER_FORMATS,
                verbose: false,
            });

            const live = scannerRef.current;
            let didPause = false;

            try {
                if (live?.isScanning) {
                    try {
                        live.pause(true);
                        didPause = true;
                    } catch {
                        // continue — decode can still succeed off-camera
                    }
                }

                const decoded = await decoder.scanFile(file, false);
                handledRef.current = false;
                emitScan(decoded);
                return { ok: true };
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : String(err);
                setErrorMessage(message || 'Decode failed');
                return { ok: false, reason: 'decode_failed', message };
            } finally {
                try {
                    decoder.clear();
                } catch {
                    // ignore
                }
                if (didPause && live) {
                    try {
                        live.resume();
                    } catch {
                        // ignore
                    }
                }
                setIsDecodingFile(false);
            }
        },
        [elementId, emitScan]
    );

    const scanFromFile = useCallback(
        async (file: File) => {
            const result = await decodeImageFile(file);
            return result.ok;
        },
        [decodeImageFile]
    );

    const captureAndScan = useCallback(async (): Promise<CaptureScanResult> => {
        const video = findScannerVideo(elementId);
        if (!video) {
            setErrorMessage('Camera video element not found');
            return { ok: false, reason: 'not_ready' };
        }

        const ready = await waitForVideoFrame(video);
        if (!ready) {
            setErrorMessage('Camera frame not ready');
            return { ok: false, reason: 'not_ready' };
        }

        // Ensure playback so current frame is available (esp. after pause / desktop)
        try {
            if (video.paused) {
                await video.play();
            }
        } catch {
            // ignore autoplay restrictions; drawImage may still work
        }

        const blob = await grabVideoFrameBlob(video);
        if (!blob) {
            setErrorMessage('Could not grab camera frame');
            return { ok: false, reason: 'capture_failed' };
        }

        const file = new File([blob], `scan-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        return decodeImageFile(file);
    }, [elementId, decodeImageFile]);

    return {
        status,
        errorMessage,
        isDecodingFile,
        stopScanner,
        scanFromFile,
        captureAndScan,
        decodeImageFile,
    };
};
